import React, { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronDown, Info, Minus } from 'lucide-react'
import Tooltip from '../../ui/Tooltip'
import {
  BrandRow, ExposureTotals, formatAreaSeconds, formatMoney, formatSeconds,
  formatSharePct
} from '../broadcastValuation'

interface Props {
  rows: BrandRow[]
  totals: ExposureTotals
  currency: string
  priced: boolean
  onSelect?: (brandKey: string) => void
}

/**
 * Every counted brand, ranked by what it was worth.
 *
 * ORDERED BY AREA-SECONDS, NOT TIME. The ranking the report sells is the one
 * the table has to be sorted by, or the reader builds an impression from the
 * order before reaching the column that contradicts it.
 *
 * The time column carries its own rank beside it so the disagreement is legible
 * row by row, not only in the slope chart above. The chart makes the pattern
 * visible at a glance; this makes any single row checkable.
 *
 * SPONSORS ONLY, and no group headings. The page narrowed to sponsors, at
 * which point a "SPONSORED" band above every row and an "UNSPONSORED" band
 * that could never render were labelling a distinction the table no longer
 * contains. The title says what these rows are.
 *
 * Rank is still against the whole field, so a sponsor at 7 is 7th of
 * everything detected rather than 3rd of the sponsors — the number means
 * "ahead of all these other brands", which is the claim worth making.
 *
 * NO MEDIAN COLUMN YET. The mean area share is all the API returns, and on a
 * skewed distribution a mean alone overstates brands carried by a few large
 * shots. The column is deliberately absent rather than filled with the mean
 * twice over — see backlog #40.
 */
type SortField = 'brand' | 'area' | 'value'

const ExposureValueTable: React.FC<Props> = ({ rows, totals, currency, priced, onSelect }) => {
  const [sortBy, setSortBy] = useState<SortField>('area')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <p className="text-sm text-gray-500">No brands counted on this broadcast.</p>
      </div>
    )
  }

  const maxArea = Math.max(...rows.map((r) => r.areaSeconds), 1e-9)

  /**
   * Sorting is local. The page hands rows in rank order and that stays the
   * default; re-ordering is a reading preference, not a change to the data.
   *
   * RANKS DO NOT MOVE WITH THE SORT. valueRank and timeRank are properties
   * of the brand within this broadcast, so sorting by name must not
   * renumber them — otherwise "time rank 1" would mean whichever row
   * happened to float to the top.
   */
  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      // Names read A-Z, measures read biggest first — which is the answer
      // someone is after when they click a number column.
      setSortOrder(field === 'brand' ? 'asc' : 'desc')
    }
  }

  const sorted = useMemo(() => {
    const dir = sortOrder === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      if (sortBy === 'brand') {
        return (a.display_name ?? a.brand_name)
          .localeCompare(b.display_name ?? b.brand_name, undefined, { sensitivity: 'base' }) * dir
      }
      const av = sortBy === 'area' ? a.areaSeconds : a.value
      const bv = sortBy === 'area' ? b.areaSeconds : b.value
      // An unpriced brand sorts last either way: it has no value, which is
      // not the same as the lowest one.
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir
    })
  }, [rows, sortBy, sortOrder])

  const sortChevron = (field: SortField) => (
    <ChevronDown className={`h-3 w-3 transition-transform ${
      sortBy === field ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : 'opacity-30'
    }`} />
  )

  const renderRow = (r: BrandRow) => (
              <tr
                key={r.brand_key}
                onClick={onSelect ? () => onSelect(r.brand_key) : undefined}
                className={`border-b border-gray-50 ${
                  onSelect ? 'cursor-pointer hover:bg-blue-50/40' : ''
                }`}
              >
                <td className="px-5 py-1.5 font-medium text-gray-800 truncate max-w-[220px]">
                  {r.display_name ?? r.brand_name}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-gray-800">
                  {formatAreaSeconds(r.areaSeconds)}
                </td>
                <td className="px-2 py-1.5">
                  {/* The bar is the only way the long tail reads as a tail: the
                      top brand is often 50x the median, which numbers alone
                      flatten. */}
                  <span className="block h-1.5 rounded-full bg-blue-500/70"
                        style={{ width: `${Math.max((r.areaSeconds / maxArea) * 100, 1.5)}%` }} />
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                  {r.equivalents.toFixed(2)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                  {formatSeconds(r.seconds)}
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center justify-center gap-1 tabular-nums">
                    <span className="text-gray-500">{r.timeRank}</span>
                    {r.rankShift > 1 ? (
                      <span className="inline-flex items-center text-emerald-600 font-medium">
                        <ArrowUp className="h-3 w-3" />{r.rankShift}
                      </span>
                    ) : r.rankShift < -1 ? (
                      <span className="inline-flex items-center text-red-600 font-medium">
                        <ArrowDown className="h-3 w-3" />{Math.abs(r.rankShift)}
                      </span>
                    ) : (
                      <Minus className="h-3 w-3 text-gray-300" />
                    )}
                  </div>
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                  {formatSharePct(r.max_area_share)}
                </td>
                {priced && (
                  <td className="px-5 py-1.5 text-right tabular-nums font-semibold text-emerald-800">
                    {formatMoney(r.value, currency)}
                  </td>
                )}
              </tr>
  )

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-5 pt-5 pb-3 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-800">Calculated sponsor value</h3>
        <Tooltip
          maxWidth={280}
          content={
            'Ranked by area-seconds. The time column shows each brand’s rank on ' +
            'seconds alone, so you can see where the two measures disagree.'
          }
        >
          <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
        </Tooltip>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-y border-gray-100 bg-gray-50/60 text-gray-500 text-[10px] uppercase tracking-wider">
              <th className="text-left font-semibold px-5 py-2 cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('brand')}>
                <div className="flex items-center gap-1">Brand {sortChevron('brand')}</div>
              </th>
              <th className="text-right font-semibold px-2 py-2 cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('area')}>
                <div className="flex items-center justify-end gap-1">Area-sec {sortChevron('area')}</div>
              </th>
              <th className="px-2 py-2 w-24" aria-hidden="true" />
              <th className="text-right font-semibold px-2 py-2 whitespace-nowrap">
                :30 Equivalent
              </th>
              <th className="text-right font-semibold px-2 py-2">On screen</th>
              <th className="text-center font-semibold px-2 py-2">
                <Tooltip content="Rank by time on screen, and how far it moves against rank by value.">
                  <span className="cursor-help">Time rank</span>
                </Tooltip>
              </th>
              <th className="text-right font-semibold px-2 py-2 whitespace-nowrap">Peak logo size</th>
              {/* LAST, because it is the conclusion. Everything to its left
                  is the working — how much screen, for how long, how
                  prominent — and a reader scanning for the answer should not
                  have to find it in the middle of the evidence. */}
              {priced && (
                <th className="text-right font-semibold px-5 py-2 text-emerald-700 cursor-pointer hover:text-emerald-800"
                    onClick={() => handleSort('value')}>
                  <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                    Sponsor value {sortChevron('value')}
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => renderRow(r))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50/60 font-semibold text-gray-800">
              <td className="px-5 py-2">Total</td>
              <td className="px-2 py-2 text-right tabular-nums">
                {formatAreaSeconds(totals.areaSeconds)}
              </td>
              <td />
              <td className="px-2 py-2 text-right tabular-nums">
                {totals.equivalents.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-right tabular-nums">
                {formatSeconds(totals.seconds)}
              </td>
              <td /><td />
              {priced && (
                <td className="px-5 py-2 text-right tabular-nums text-emerald-800">
                  {formatMoney(totals.value, currency)}
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

export default ExposureValueTable
